import { PageTreeItem } from "../../models/PageTreeItem";
import { GetPagesResponse, Page as ResponsePage } from "../../models/services/GetPagesResponse";
import type { PageTreeNode } from "../../../../ds";

/** Tree helpers of the Pages screen: the page list as the server sends it, mapped, searched and changed optimistically. */

export function removePageTreeItemById(tree: PageTreeItem[], pageId: number): number | null {
    const rootIndex = tree.findIndex((node) => node.pageId === pageId);
    if (rootIndex !== -1) {
        tree.splice(rootIndex, 1); // Remove the item from the root array
        return null; // Root-level items don't have a parent, return null
    }

    for (let i = 0; i < tree.length; i++) {
        const node = tree[i];
        if (node.children) {
            const childIndex = node.children.findIndex((child) => child.pageId === pageId);
            if (childIndex !== -1) {
                node.children.splice(childIndex, 1);
                return node.pageId; // Return the parent's ID
            }
            const result = removePageTreeItemById(node.children, pageId);
            if (result !== null) return result;
        }
    }

    return null; // Return null if not found
}

export const mapGetPagesResponseToPages = (response: GetPagesResponse): PageTreeItem[] => {
    const mapToPageTreeItem = (page: ResponsePage): PageTreeItem => ({
        title: page.title,
        spaceId: page.spaceId,
        pageId: page.pageId,
        dateModified: page.dateModified,
        dateCreated: page.dateCreated,
        children: page.children?.map(mapToPageTreeItem),
    });
    return response.pages.map(mapToPageTreeItem);
};

export function findInTree(tree: PageTreeItem[], id: number): PageTreeItem | null {
    for (const node of tree) {
        if (node.pageId === id) return node;
        if (node.children) {
            const found = findInTree(node.children, id);
            if (found) return found;
        }
    }
    return null;
}

export function isDescendant(tree: PageTreeItem[], ancestorId: number, candidateId: number): boolean {
    const ancestor = findInTree(tree, ancestorId);
    if (!ancestor?.children) return false;
    return treeContainsPageId(ancestor.children, candidateId);
}

export function treeContainsPageId(tree: PageTreeItem[], id: number): boolean {
    for (const n of tree) {
        if (n.pageId === id) return true;
        if (n.children && treeContainsPageId(n.children, id)) return true;
    }
    return false;
}

export function findParentInTree(tree: PageTreeItem[], id: number): PageTreeItem | null {
    for (const node of tree) {
        if (node.children?.some((c) => c.pageId === id)) return node;
        if (node.children) {
            const found = findParentInTree(node.children, id);
            if (found) return found;
        }
    }
    return null;
}

/** The ancestors of a page, root first (empty for a root page or a page that is not in the tree). */
export function ancestorsOf(tree: PageTreeItem[], id: number, trail: PageTreeItem[] = []): PageTreeItem[] | undefined {
    for (const node of tree) {
        if (node.pageId === id) return trail;
        if (node.children) {
            const found = ancestorsOf(node.children, id, [...trail, node]);
            if (found) return found;
        }
    }
    return undefined;
}

export function countPages(tree: PageTreeItem[]): number {
    return tree.reduce((n, p) => n + 1 + (p.children ? countPages(p.children) : 0), 0);
}

export function optimisticReorder(tree: PageTreeItem[], draggedId: number, targetId: number, position: "before" | "after"): PageTreeItem[] {
    const cloned: PageTreeItem[] = structuredClone(tree);
    let draggedItem: PageTreeItem | null = null;

    const remove = (nodes: PageTreeItem[]): boolean => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].pageId === draggedId) {
                draggedItem = nodes.splice(i, 1)[0];
                return true;
            }
            if (nodes[i].children && remove(nodes[i].children!)) return true;
        }
        return false;
    };

    const insert = (nodes: PageTreeItem[]): boolean => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].pageId === targetId) {
                nodes.splice(position === "before" ? i : i + 1, 0, draggedItem!);
                return true;
            }
            if (nodes[i].children && insert(nodes[i].children!)) return true;
        }
        return false;
    };

    remove(cloned);
    if (draggedItem) insert(cloned);
    return cloned;
}

export function optimisticReparent(tree: PageTreeItem[], draggedId: number, targetId: number): PageTreeItem[] {
    const cloned: PageTreeItem[] = structuredClone(tree);
    let draggedItem: PageTreeItem | null = null;

    const remove = (nodes: PageTreeItem[]): boolean => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].pageId === draggedId) {
                draggedItem = nodes.splice(i, 1)[0];
                return true;
            }
            if (nodes[i].children && remove(nodes[i].children!)) return true;
        }
        return false;
    };

    const addToTarget = (nodes: PageTreeItem[]): boolean => {
        for (const node of nodes) {
            if (node.pageId === targetId) {
                node.children = [...(node.children ?? []), draggedItem!];
                return true;
            }
            if (node.children && addToTarget(node.children)) return true;
        }
        return false;
    };

    remove(cloned);
    if (draggedItem) addToTarget(cloned);
    return cloned;
}

export function toTreeNodes(pages: PageTreeItem[]): PageTreeNode[] {
    return pages.map((p) => ({
        id: String(p.pageId),
        title: p.title,
        children: p.children && p.children.length ? toTreeNodes(p.children) : undefined,
    }));
}
